import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown } from "lucide-react";
import Logo from "@/components/Logo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1709548145082-04d0cde481d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxwdWIlMjBpbnRlcmlvciUyMHdhcm0lMjBicmljayUyMGxpZ2h0aW5nJTIwbmlnaHR8ZW58MHx8fHwxNzkxODgyMTk5fDA&ixlib=rb-4.1.0&q=85";

function RevealLine({ children, delay, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden grain"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Interno del pub Lateresina con mattoni e luci calde"
          className="w-full h-[120%] object-cover"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(13,13,15,0.55), rgba(13,13,15,0.94))" }}
        />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-24 w-full">
        <motion.div
          initial={{ opacity: 0, scale: 0.75, rotate: -10 }}
          animate={{ opacity: 1, scale: 1, rotate: -3 }}
          transition={{ delay: 0.2, duration: 0.7, ease: "easeOut" }}
          className="text-[#F5F2EB] mb-10 inline-block"
        >
          <Logo />
        </motion.div>

        <h1 className="font-display uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl">
          <RevealLine delay={0.5}>Hamburger.</RevealLine>
          <RevealLine delay={0.68} className="text-[#D34E36]">Birra alla spina.</RevealLine>
          <RevealLine delay={0.86}>DJ set fino a tardi.</RevealLine>
        </h1>

        <RevealLine delay={1.1} className="mt-7 max-w-xl">
          <p className="text-base sm:text-lg text-stone-300 leading-relaxed">
            Il pub di Piazza XX Settembre, nel cuore di Nizza Monferrato. Tagliata di fassona,
            tacos francesi, birre Kozel e San Benoit spillate al momento.
          </p>
        </RevealLine>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.6 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href="#menu"
            data-testid="hero-menu-button"
            className="bg-[#D34E36] hover:bg-[#F49D37] hover:text-black text-[#F5F2EB] font-bold uppercase tracking-widest text-xs px-7 py-3.5 rounded-full transition-colors"
          >
            Scopri il menù
          </a>
          <a
            href="#contatti"
            data-testid="hero-contact-button"
            className="border border-white/25 hover:border-[#F49D37] hover:text-[#F49D37] text-[#F5F2EB] font-bold uppercase tracking-widest text-xs px-7 py-3.5 rounded-full transition-colors"
          >
            Prenota un tavolo
          </a>
          <span
            data-testid="hero-hours-badge"
            className="flex items-center gap-2 text-[11px] font-mono2 uppercase tracking-widest text-stone-300"
          >
            <span className="w-2 h-2 rounded-full bg-[#F49D37] animate-pulse" />
            Aperto stasera · 19:00–02:00
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.55 }}
          className="mt-14 flex items-center gap-2 text-stone-400 text-[11px] font-mono2 uppercase tracking-widest"
        >
          <MapPin size={14} className="text-[#D34E36]" />
          Piazza XX Settembre 22/23 · Nizza Monferrato
        </motion.div>
      </motion.div>

      <motion.a
        href="#menu"
        data-testid="hero-scroll-hint"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-stone-500 hover:text-[#F49D37] transition-colors"
        aria-label="Scorri al menù"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.a>
    </section>
  );
}
